import React from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { login } from "../../services/UserService";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useTheme,
  InputAdornment,
  IconButton,
  Collapse,
} from "@mui/material";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import { tokens } from "../../theme";

const initialValues = {
  username: "",
  password: "",
};

const userSchema = yup.object().shape({
  username: yup.string().required("This field is required!"),
  password: yup.string().required("This field is required!"),
});

const LoginPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  // phase: null | "loading1" | "success" | "loading2" | "error"
  const [phase, setPhase] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  const handleFormSubmit = async (values, { setSubmitting, setErrors }) => {
    setErrorMessage("");
    setPhase("loading1");
    setSubmitting(true);
    try {
      const response = await login(values.username, values.password);
      if (response.data.token) {
        // success: keep spinner for 3s, then show success, then spinner+redirect
        setTimeout(() => {
          setPhase("success");
        }, 3000);
        setTimeout(() => {
          setPhase("loading2");

          if (
            response.data.message &&
            response.data.message.includes("change your password")
          ) {
            navigate("/password-reset");
          } else {
            navigate("/dashboard");
          }
          // no setSubmitting(false) needed since unmount
        }, 4000);
      } else {
        // login failed: keep spinner for 3s, then show error for 3s, then reset
        const msg = response.message || "Login failed";
        setTimeout(() => {
          setPhase("error");
          setErrorMessage(msg);
          setSubmitting(false);
          // after showing error for 3s, clear phase and errorMessage
          setTimeout(() => {
            setPhase(null);
            setErrorMessage("");
            setErrors({}); // clear Formik errors if any
          }, 3000);
        }, 3000);
      }
    } catch (error) {
      // exception: keep spinner for 3s, then show error for 3s, then reset
      const msg = error.response?.data?.message || error.message;
      setTimeout(() => {
        setPhase("error");
        setErrorMessage(msg);
        setSubmitting(false);
        setTimeout(() => {
          setPhase(null);
          setErrorMessage("");
          setErrors({});
        }, 3000);
      }, 3000);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: userSchema,
    onSubmit: handleFormSubmit,
  });

  const showAlert =
    phase === "success"
      ? { severity: "success", text: "Logged in successfully" }
      : phase === "error"
        ? { severity: "error", text: errorMessage }
        : formik.touched.username && formik.errors.username
          ? { severity: "error", text: formik.errors.username }
          : null;

  // Safe fallbacks to preserve styling during the Collapse exit animation when showAlert becomes null
  const alertSeverity = showAlert?.severity || "error";
  const alertText = showAlert?.text || "";
  const isSuccess = alertSeverity === "success";

  return (
    <Box
      className="app"
      display="flex"
      width="100vw"
      height="100vh"
      sx={{ backgroundColor: colors.primary[500] }}
    >
      <Box className="content" display="flex" flexGrow={1}>
        <Container
          maxWidth="xs"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100%",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              minWidth: { xs: "100%", sm: "320px" },
              width: "100%",
              padding: { xs: "32px 20px", sm: "40px 32px" },
              borderRadius: "16px",
              backgroundColor:
                theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 12px 40px rgba(0, 0, 0, 0.4)"
                  : "0px 12px 40px rgba(0, 0, 0, 0.08)",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
              animation: "fadeIn 0.6s ease-out forwards",
            }}
          >
            <Typography
              variant="h1"
              textAlign="center"
              fontWeight="800"
              color={colors.gray[100]}
              sx={{ letterSpacing: "-0.5px", mb: 1 }}
            >
              BFB<span style={{ color: colors.blueAccent[500] }}>.</span>
            </Typography>
            <Typography
              variant="subtitle1"
              textAlign="center"
              color={colors.gray[400]}
              fontWeight="500"
              mb={4}
            >
              Welcome back. Please enter your details.
            </Typography>

            <Collapse in={!!showAlert}>
              <Alert
                severity={alertSeverity}
                sx={{
                  backgroundColor: isSuccess
                    ? theme.palette.mode === "dark"
                      ? "rgba(76, 206, 172, 0.15)"
                      : "rgba(16, 185, 129, 0.1)"
                    : theme.palette.mode === "dark"
                      ? "rgba(244, 63, 94, 0.15)"
                      : "rgba(239, 68, 68, 0.1)",
                  color: isSuccess
                    ? colors.greenAccent[500]
                    : colors.redAccent[500],
                  border: `1px solid ${isSuccess ? colors.greenAccent[500] : colors.redAccent[500]}`,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  mb: 3,
                  borderRadius: "8px",
                  "& .MuiAlert-icon": {
                    color: isSuccess
                      ? colors.greenAccent[500]
                      : colors.redAccent[500],
                  },
                }}
              >
                {alertText}
              </Alert>
            </Collapse>

            <form onSubmit={formik.handleSubmit}>
              <TextField
                label="Username"
                variant="filled"
                fullWidth
                margin="normal"
                name="username"
                type="text"
                value={formik.values.username}
                onChange={formik.handleChange}
                error={
                  formik.touched.username && Boolean(formik.errors.username)
                }
                helperText={formik.touched.username && formik.errors.username}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: "0.95rem",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.04)",
                    border: "1px solid transparent",
                    transition: "all 0.2s ease",
                    "& input:-webkit-autofill": {
                      WebkitBorderRadius: "8px",
                      borderRadius: "8px",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.06)",
                    },
                    "&.Mui-focused": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.02)",
                      borderColor: colors.blueAccent[500],
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.95rem", color: colors.gray[400] },
                }}
              />

              <TextField
                label="Password"
                variant="filled"
                fullWidth
                margin="normal"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: "0.95rem",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.04)",
                    border: "1px solid transparent",
                    transition: "all 0.2s ease",
                    "& input:-webkit-autofill": {
                      WebkitBorderRadius: "8px",
                      borderRadius: "8px",
                      transition: "background-color 5000s ease-in-out 0s",
                    },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.06)",
                    },
                    "&.Mui-focused": {
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(0,0,0,0.02)",
                      borderColor: colors.blueAccent[500],
                    },
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{
                          color: colors.gray[400],
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:active": { transform: "scale(0.8)" },
                          "&:hover": { color: colors.gray[300] },
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlined />
                        ) : (
                          <VisibilityOutlined />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.95rem", color: colors.gray[400] },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={
                  !formik.values.username ||
                  !formik.values.password ||
                  formik.isSubmitting
                }
                sx={{
                  marginTop: "32px",
                  padding: "12px 0",
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "8px",
                  backgroundColor: colors.blueAccent[600],
                  color: "#ffffff",
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 4px 14px rgba(99, 102, 241, 0.4)"
                      : "0 4px 14px rgba(79, 70, 229, 0.3)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: colors.blueAccent[500],
                    transform: "translateY(-2px)",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 6px 20px rgba(99, 102, 241, 0.5)"
                        : "0 6px 20px rgba(79, 70, 229, 0.4)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(0,0,0,0.12)",
                    color:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(0,0,0,0.3)",
                    boxShadow: "none",
                  },
                }}
              >
                {phase === "loading1" || phase === "loading2" ? (
                  <>
                    Authenticating&nbsp;
                    <CircularProgress
                      size={18}
                      sx={{ color: "inherit", ml: "12px" }}
                    />
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </Box>
        </Container>
      </Box>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default LoginPage;
